import React, { useEffect, useState } from "react";
import { Input, Modal, Select, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DEFAULT_BOARD_COLUMN_COLORS, STATUS_CATEGORIES } from "../../constants/projects.constants";
import { errMsg } from "../../helpers/errMsg.helper";
import FieldLabel from "../../components/FieldLabel";
import FieldError from "../../components/FieldError";
import type { Status, StatusCategory } from "../../../../core/data/redux/projects/types";

interface ColumnSettingsProps {
  statuses: Status[];
  canManage?: boolean;
  onReorder: (ids: number[]) => Promise<void>;
  onCreate: (p: { name: string; category: StatusCategory; color: string }) => Promise<void>;
  onUpdate: (id: number, p: { name?: string; category?: StatusCategory; color?: string }) => Promise<void>;
  onDelete: (s: Status) => Promise<void>;
}

const ColumnSettings: React.FC<ColumnSettingsProps> = ({
  statuses, canManage = false, onReorder, onCreate, onUpdate, onDelete,
}) => {
  const [items, setItems] = useState<Status[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => { setItems(statuses); }, [statuses]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!canManage) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((s) => s.id === active.id);
    const newIdx = items.findIndex((s) => s.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const newItems = arrayMove(items, oldIdx, newIdx);
    setItems(newItems);
    try {
      setSavingOrder(true);
      await onReorder(newItems.map((s) => s.id));
    } catch {
      setItems(items);
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <i className="ti ti-layout-columns text-info" />
            Workflow Columns
          </h5>
          <span className="text-muted small">
            Drag to reorder. Add or remove columns to fit your workflow.
          </span>
        </div>
        {canManage && (
          <button
            type="button"
            className="btn btn-info btn-sm"
            style={{ borderRadius: 6 }}
            onClick={() => setCreateOpen(true)}
          >
            <i className="ti ti-plus me-1" />Add Column
          </button>
        )}
      </div>

      {savingOrder && (
        <div style={{
          padding: "8px 12px", background: "#eff6ff", borderRadius: 8,
          color: "#3b82f6", fontSize: 13, marginBottom: 12,
        }}>
          <i className="ti ti-loader me-2" />Saving order…
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="d-flex flex-column gap-2">
            {items.map((status) => (
              <SortableRow key={status.id} status={status} canManage={canManage} onUpdate={onUpdate} onDelete={onDelete} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <CreateColumnModal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onSubmit={async (p) => { await onCreate(p); setCreateOpen(false); }}
      />
    </div>
  );
};

/* ─── SortableRow ─────────────────────────────────────────────────────────── */

interface SortableRowProps {
  status: Status;
  canManage?: boolean;
  onUpdate: (id: number, p: { name?: string; category?: StatusCategory; color?: string }) => Promise<void>;
  onDelete: (s: Status) => Promise<void>;
}

const SortableRow: React.FC<SortableRowProps> = ({ status, canManage = false, onUpdate, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: status.id });

  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(status.name);
  const [category, setCategory] = useState<StatusCategory>(status.category);
  const [color, setColor] = useState<string>(status.color || "#6B7280");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(status.name);
    setCategory(status.category);
    setColor(status.color || "#6B7280");
  }, [status]);

  const dndStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  /* Hover-driven row style */
  const rowStyle: React.CSSProperties = {
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${hovered && !editing ? "#c7d7f0" : "#e2e8f0"}`,
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    background: hovered && !editing ? "#f0f7ff" : "#fff",
    transition: "background 0.18s, border-color 0.18s, box-shadow 0.18s",
    boxShadow: hovered && !editing ? "0 2px 8px 0 rgba(59,130,246,0.08)" : "none",
  };

  const handleSave = async () => {
    try { setSaving(true); await onUpdate(status.id, { name, category, color }); setEditing(false); }
    finally { setSaving(false); }
  };

  const handleDeleteClick = () => {
    Modal.confirm({
      title: `Delete column '${status.name}'?`,
      content: "If issues use this column you will be asked to choose a replacement.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => onDelete(status),
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={rowStyle}>
        {/* Drag handle */}
        <button
          type="button"
          className="btn btn-sm btn-light"
          style={{
            cursor: "grab",
            borderRadius: 6,
            background: hovered ? "#dbeafe" : undefined,
            border: "none",
            transition: "background 0.18s",
          }}
          {...attributes}
          {...listeners}
          title="Drag to reorder"
        >
          <i className="ti ti-grip-vertical" style={{ color: hovered ? "#3b82f6" : "#94a3b8" }} />
        </button>

        {/* Color dot */}
        <Tooltip title={`Color: ${status.color || "#6B7280"}`}>
          <span
            className="d-inline-block rounded-circle"
            style={{
              width: 14, height: 14,
              background: status.color || "#6B7280",
              flexShrink: 0,
              border: "2px solid #fff",
              boxShadow: "0 0 0 1px #e2e8f0",
            }}
          />
        </Tooltip>

        {editing ? (
          <>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ maxWidth: 220, borderRadius: 8, background: "#f8fafc" }}
              size="large"
            />
            <Select
              value={category}
              onChange={(v) => setCategory(v as StatusCategory)}
              options={STATUS_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
              style={{ width: 160 }}
              size="large"
            />
            <Select
              value={color}
              onChange={(v) => setColor(v)}
              options={DEFAULT_BOARD_COLUMN_COLORS.map((c) => ({
                value: c,
                label: (
                  <span className="d-inline-flex align-items-center gap-2">
                    <span className="d-inline-block rounded-circle" style={{ width: 10, height: 10, background: c }} />
                    {c}
                  </span>
                ),
              }))}
              style={{ width: 140 }}
              size="large"
            />
            <button
              type="button" className="btn btn-sm btn-info" style={{ borderRadius: 6 }}
              onClick={handleSave} disabled={saving || !name.trim()}
            >
              Save
            </button>
            <button
              type="button" className="btn btn-sm btn-light" style={{ borderRadius: 6 }}
              onClick={() => setEditing(false)} disabled={saving}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <div className="flex-grow-1">
              <div className="fw-semibold" style={{ fontSize: 13.5, color: "#1e293b" }}>
                {status.name}
              </div>
              <div className="text-muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {status.category.replace("_", " ")}
              </div>
            </div>

            {canManage && (
              <div
                style={{
                  display: "flex", gap: 6,
                  opacity: hovered ? 1 : 0,
                  transition: "opacity 0.18s",
                  pointerEvents: hovered ? "auto" : "none",
                }}
              >
                <Tooltip title="Edit column">
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                      borderRadius: 6,
                      background: "#fef9ec",
                      border: "1px solid #fde68a",
                      color: "#f59e0b",
                    }}
                    onClick={() => setEditing(true)}
                  >
                    <i className="ti ti-edit" />
                  </button>
                </Tooltip>
                <Tooltip title="Delete column">
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                      borderRadius: 6,
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      color: "#ef4444",
                    }}
                    onClick={handleDeleteClick}
                  >
                    <i className="ti ti-trash" />
                  </button>
                </Tooltip>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ─── CreateColumnModal ───────────────────────────────────────────────────── */

interface CreateColumnModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (p: { name: string; category: StatusCategory; color: string }) => Promise<void>;
}

const CreateColumnModal: React.FC<CreateColumnModalProps> = ({ open, onCancel, onSubmit }) => {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<StatusCategory>("TODO");
  const [color, setColor] = useState<string>(DEFAULT_BOARD_COLUMN_COLORS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(""); setCategory("TODO");
      setColor(DEFAULT_BOARD_COLUMN_COLORS[0]); setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    try {
      setSubmitting(true); setError(null);
      await onSubmit({ name: name.trim(), category, color });
    } catch (e: any) {
      setError(errMsg(e, "Failed to create column"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      width="640px"
      zIndex={1001}
      styles={{
        content: {
          padding: 0,
          borderRadius: 12,
          overflow: "hidden",
        },
        header: {
          padding: "14px 20px 12px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 0,
        },
        body: {
          padding: "16px 24px 20px",
        },
        footer: {
          padding: "10px 20px 14px",
          borderTop: "1px solid #f0f0f0",
          marginTop: 0,
        },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: "#3b82f618",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#3b82f6", fontSize: 15,
          }}>
            <PlusOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>Add Column</div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", fontWeight: 400 }}>
              Create a new workflow column
            </div>
          </div>
        </div>
      }
      footer={
        <div style={{ padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            className="btn btn-light border px-4"
            style={{ borderRadius: 8 }}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="btn px-4"
            style={{ borderRadius: 8, background: "#10b981", color: "#fff", fontWeight: 600 }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <><span className="spinner-border spinner-border-sm me-2" />Adding...</>
            ) : (
              <><PlusOutlined className="me-2" />Add Column</>
            )}
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-3">
        {error && (
          <div style={{
            padding: "10px 14px", background: "#fef2f2",
            border: "1px solid #fecaca", borderRadius: 8,
            color: "#dc2626", fontSize: 13,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="ti ti-alert-triangle" style={{ fontSize: 16 }} />
            {error}
          </div>
        )}

        <div>
          <FieldLabel required>Name</FieldLabel>
          <Input
            size="large"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Code Review"
            status={error && !name.trim() ? "error" : ""}
            style={{ borderRadius: 8, background: "#f8fafc" }}
          />
          <FieldError message={error && !name.trim() ? error : undefined} />
        </div>

        <div>
          <FieldLabel>Category</FieldLabel>
          <Select
            size="large"
            style={{ width: "100%" }}
            value={category}
            onChange={(v) => setCategory(v as StatusCategory)}
            options={STATUS_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
          />
          <span className="text-muted small">Used for burndown and "is done" detection.</span>
        </div>

        <div>
          <FieldLabel>Color</FieldLabel>
          <Select
            size="large"
            style={{ width: "100%" }}
            value={color}
            onChange={(v) => setColor(v)}
            options={DEFAULT_BOARD_COLUMN_COLORS.map((c) => ({
              value: c,
              label: (
                <span className="d-inline-flex align-items-center gap-2">
                  <span className="d-inline-block rounded-circle" style={{ width: 12, height: 12, background: c }} />
                  {c}
                </span>
              ),
            }))}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ColumnSettings;